/**
 * Zod schema for the READING assignment document — client-side only, the backend stores `data`
 * blind. A MATH template's exercise set has its own schema (`math-practice-schema.ts`);
 * `documentSchemaFor(type)` picks between them.
 *
 * Shares `ContentBlock` and `Screen` (with its "content or activities" refinement) from
 * `lib/assessments/schema.ts`. Does NOT share `SCHEMA_VERSION` / `Assessment`: the assessment
 * version check would make an assessments-only bump break every stored assignment. Activities and
 * rubrics are not authored here, but a stored document carrying activities still parses.
 *
 * Issue paths mirror the assessments schema: `screens.<i>.id` at definition level,
 * `definition.screens.<i>.id` at document level.
 */
import { z } from 'zod'

import { AssignmentType, Grade } from '../../model/assignments/enums'
import { MathPracticeDocumentSchema } from './math-practice-schema'
import { ContentBlock, Screen } from '../assessments/schema'

/** Independent of the assessments `SCHEMA_VERSION`; both are `1` by coincidence. */
export const ASSIGNMENT_SCHEMA_VERSION = 1

const AssignmentDefinitionObject = z.object({
  id: z.string(),
  schemaVersion: z.number().int(),
  /** The kid-facing book name — not the row's catalogue `title`. */
  title: z.string(),
  description: z.string().optional(),
  disciplineCode: z.enum(['MATH', 'ELA']),
  grade: z.enum(Grade),
  /** Chapters are plain screens; there is no chapter `title` — the heading lives in the text block. */
  screens: z.array(Screen),
})

type AssignmentDefinitionShape = z.output<typeof AssignmentDefinitionObject>

type AddIssue = (issue: { code: 'custom', message: string, path: (string | number)[] }) => void

/**
 * Exact version match required in both directions: newer would lose unknown fields on the next
 * full-replacement save; older would be re-stamped without an upcaster.
 */
const refineSchemaVersion = (definition: AssignmentDefinitionShape, addIssue: AddIssue): void => {
  if (definition.schemaVersion === ASSIGNMENT_SCHEMA_VERSION) {
    return
  }
  addIssue({
    code: 'custom',
    path: ['schemaVersion'],
    message: definition.schemaVersion > ASSIGNMENT_SCHEMA_VERSION
      ? `Assignment schema version ${definition.schemaVersion} is newer than this assignment editor supports (${ASSIGNMENT_SCHEMA_VERSION}). Update app-admin before editing this assignment.`
      : `Assignment schema version ${definition.schemaVersion} is older than this assignment editor supports (${ASSIGNMENT_SCHEMA_VERSION}). This assignment editor has no upcaster.`,
  })
}

/** Duplicate chapter ids are reported on every repeat occurrence at `screens.<i>.id`. */
const refineScreenIds = (definition: AssignmentDefinitionShape, addIssue: AddIssue): void => {
  const seen = new Set<string>()
  definition.screens.forEach((screen, screenIdx) => {
    if (seen.has(screen.id)) {
      addIssue({
        code: 'custom',
        path: ['screens', screenIdx, 'id'],
        message: `Duplicate chapter id "${screen.id}" — chapter ids must be unique.`,
      })
      return
    }
    seen.add(screen.id)
  })
}

const refineDefinition = (definition: AssignmentDefinitionShape, addIssue: AddIssue): void => {
  refineSchemaVersion(definition, addIssue)
  refineScreenIds(definition, addIssue)
}

export const AssignmentDefinition = AssignmentDefinitionObject.superRefine((definition, ctx) => {
  refineDefinition(definition, issue => ctx.addIssue(issue))
})

type ParsedAssignmentDefinition = z.infer<typeof AssignmentDefinition>
export type ParsedChapter = ParsedAssignmentDefinition['screens'][number]
export type ParsedContentBlock = z.infer<typeof ContentBlock>

/** `data` as stored: `{ definition, media? }`. What the editor validates, so issue paths are document-rooted. */
export const AssignmentTemplateDocumentSchema = z.object({
  definition: AssignmentDefinition,
  /** Manifest of object paths. Declared for the type, deliberately unrefined — the store is its only writer. */
  media: z.array(z.string()).optional(),
})

export type ParsedAssignmentTemplateDocument = z.output<typeof AssignmentTemplateDocumentSchema>

/**
 * The template `type` selects the document schema (RND-196 decision 3 — reverses RND-60 decision 4
 * for MATH). Exhaustive: a third type fails `pnpm typecheck` until it names its schema.
 */
const DOCUMENT_SCHEMAS = {
  [AssignmentType.Reading]: AssignmentTemplateDocumentSchema,
  [AssignmentType.Math]: MathPracticeDocumentSchema,
} as const satisfies Record<AssignmentType, z.ZodType>

type AssignmentDocumentSchema = typeof DOCUMENT_SCHEMAS[AssignmentType]

export const documentSchemaFor = (type: AssignmentType): AssignmentDocumentSchema => DOCUMENT_SCHEMAS[type]

const CurriculumLesson = z.object({
  id: z.number().int(),
  lesson_number: z.number().int(),
  name: z.string(),
})

const CurriculumTopic = z.object({
  id: z.number().int(),
  topic_name: z.string(),
  lessons: z.array(CurriculumLesson),
})

/**
 * `modules` of `GET /admin-api/curriculum/modules`. `ApiResponse.data` is `any` and the resources
 * emit `topics` / `lessons` only `whenLoaded`, so the catalogue is parsed before the pickers read
 * them unguarded. Mirrors `CurriculumModuleOption`.
 */
export const CurriculumModules = z.array(z.object({
  id: z.number().int(),
  module_number: z.number().int(),
  module_name: z.string(),
  topics: z.array(CurriculumTopic),
}))
